import { useParams, useNavigate } from 'react-router-dom'
import { useViewMode } from '@/stores/viewMode'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { ScrollArea } from '@/components/ui/scroll-area'
import { ArrowLeft } from 'lucide-react'
import { format } from 'date-fns'
import { zhTW } from 'date-fns/locale'
import {
  formatExpectedPlayTime,
  useSupplierAdSchedule,
} from '@/hooks/useSupplierAdSchedule'

/**
 * Supplier-side ad-schedule template detail (read-only).
 *
 * Per issue #281, this view:
 *   - shows batches (slots) but is NOT editable
 *   - does NOT display total_duration
 *   - shows play_minute (read-only, as text)
 *   - lists ONLY this supplier's ads within each slot
 *   - shows each ad's BE-computed "預計 X分YY秒 播放" after its duration
 *
 * Source data: GET /api/ad-supplier/:supplier_id/ad-schedule-templates/:template_id
 */
export default function SupplierAdScheduleViewPage() {
  const { template_id: templateIdParam } = useParams<{ template_id: string }>()
  const { id: supplierId } = useViewMode()
  const navigate = useNavigate()

  const templateId = templateIdParam ? Number(templateIdParam) : null
  const { template, isLoading } = useSupplierAdSchedule({ supplierId, templateId })

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    )
  }

  if (!template) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">找不到模版，或本供應商在此模版內沒有任何廣告。</p>
        <Button onClick={() => navigate(-1)} className="mt-4">
          返回列表
        </Button>
      </div>
    )
  }

  // Show all slots returned by BE (BE filters to active items only; inactive
  // slots may still appear with empty item lists, matching the existing
  // provider view's behavior).
  const slots = template.slots

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{template.template_name}</h1>
            <p className="text-muted-foreground">
              建立於{' '}
              {format(new Date(template.created_at), 'yyyy年MM月dd日 HH:mm', { locale: zhTW })}
            </p>
          </div>
        </div>
        {/* No edit / apply buttons — supplier-side is read-only */}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>基本資訊</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-3">
          <div>
            <p className="text-sm text-muted-foreground">模版名稱</p>
            <p className="font-medium">{template.template_name}</p>
          </div>
          {template.description ? (
            <div>
              <p className="text-sm text-muted-foreground">描述</p>
              <p className="font-medium">{template.description}</p>
            </div>
          ) : null}
          <div>
            <p className="text-sm text-muted-foreground">狀態</p>
            {template.is_active ? (
              <Badge className="bg-green-500">啟用</Badge>
            ) : (
              <Badge variant="secondary">停用</Badge>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>梯次詳細內容</CardTitle>
          <CardDescription>僅顯示本供應商旗下的廣告，及其預計播放時間</CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[640px] pr-2">
            <div className="space-y-6">
              {slots.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">此模版沒有任何梯次</p>
              ) : (
                slots.map((slot, slotIdx) => (
                  <div key={slot.slot_id}>
                    {slotIdx > 0 && <Separator className="my-6" />}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-semibold">
                            {slot.slot_name || `梯次 ${slot.slot_number}`}
                          </h3>
                          <Badge variant="outline">梯次 {slot.slot_number}</Badge>
                          {!slot.is_active && (
                            <Badge variant="secondary">停用</Badge>
                          )}
                        </div>
                        {/* Read-only play_minute (no editable input). Total
                            duration is intentionally NOT shown per issue #281. */}
                        <div className="text-sm text-muted-foreground">
                          播放分鐘：<span className="font-medium text-foreground">{slot.play_minute}</span>
                        </div>
                      </div>

                      <div className="space-y-2">
                        {slot.slot_items.length === 0 ? (
                          <p className="text-center text-sm text-muted-foreground py-4 border border-dashed rounded-lg">
                            此梯次沒有本供應商的廣告
                          </p>
                        ) : (
                          slot.slot_items.map((item, idx) => (
                            <div
                              key={item.item_id}
                              className="flex items-center gap-3 p-3 rounded-lg bg-muted/30"
                            >
                              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-background text-sm font-medium shrink-0">
                                {idx + 1}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium truncate">
                                  {item.advertisement.title || '無標題廣告'}
                                </p>
                              </div>
                              <Badge variant="outline" className="shrink-0">
                                {item.play_duration} 秒
                              </Badge>
                              <span className="text-sm text-muted-foreground shrink-0">
                                預計 {formatExpectedPlayTime(item.expected_play_seconds)} 播放
                              </span>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  )
}
